import { AISystem } from "@/core/battle/ai/AISystem";
import { AIContext } from "@/core/battle/ai/context/AIContext";
import { AIPlan } from "@/core/battle/ai/types/ai.types";
import { CombatExecutor } from "@/core/battle/CombatExecutor";
import { ControlStateMachine } from "@/core/battle/ControlStateMachine";
import { ExecutorManager } from "@/core/battle/executors/ExecutorManager";
import { MovementExecutor } from "@/core/battle/MovementExecutor";
import { TurnManager } from "@/core/battle/TurnManager";
import { ControlState, BattleUnit, BattleStatus } from "@/core/types/battle.types";
import { useBattleStore } from "@/store/useBattleStore";

/**
 * AI Turn Manager
 * Handles execution of enemy AI turns
 * 
 * Responsibilities:
 * - Loop until unit exhausted
 * - Call AISystem for planning
 * - Execute actions via shared executors
 * - Detect infinite loops
 * - Manage turn end logic
 */
export class AITurnManager {
    private static readonly MAX_ITERATIONS = 5;
    private static readonly ACTION_DELAY = 400;
    
    constructor(
        private aiSystem: AISystem,
        private executorManager: ExecutorManager,
        private turnManager: TurnManager,
        private controlStateMachine: ControlStateMachine
    ) {}
    
    /**
     * Execute full turn for one AI unit
     * Loops until stamina exhausted or max iterations
     */
    async executeUnitTurn(unitId: string): Promise<void> {
        console.log(`[AITurnManager] ===== Starting turn for ${unitId} =====`);
        
        const startState = useBattleStore.getState().units.get(unitId);
        if (!startState) {
            console.warn(`[AITurnManager] Unit ${unitId} not found`);
            return;
        }
        
        this.logUnitState('Initial', startState);
        
        // Loop until exhausted
        let iterationCount = 0;
        const actionHistory: string[] = [];
        
        while (iterationCount < AITurnManager.MAX_ITERATIONS) {
            iterationCount++;
            
            const unit = useBattleStore.getState().units.get(unitId);

            if (!unit) break;
            // Check termination conditions
            if (this.shouldStopTurn(unit, unitId)) {
                break;
            }
            
            // Get next action from AI
            const action = this.aiSystem.getNextAction(unitId);
            
            if (!action || action.type === 'wait') {
                console.log(`[AITurnManager] Unit ${unitId} chose to wait`);
                break;
            }

            if (action.type === 'move' && action.targetPath?.cost === 0) {
                console.log(`[AITurnManager] Unit ${unitId} chose to wait (0)`);
                break;
            }
            
            actionHistory.push(action.type);
            
            // Detect infinite loops
            if (this.detectLoop(actionHistory, unitId, action)) {
                break;
            }
            
            console.log(`[AITurnManager] Iteration ${iterationCount}/${AITurnManager.MAX_ITERATIONS}:`, {
                action: action.type,
                position: `${unit!.q}:${unit!.r}`,
                stamina: unit!.currentStamina,
            }, action);

            await this.checkPause();
            
            // Execute action
            const staminaBefore = unit.currentStamina;
            await this.executeAction(action);
            
            // // Verify stamina changed (safety check)
            // if (!this.verifyStaminaChanged(unitId, staminaBefore)) {
            //     break;
            // }
            
            // Wait between actions
            await this.delay(AITurnManager.ACTION_DELAY);
        }

        // End turn
        this.finishUnitTurn(unitId, startState, actionHistory);
        
        await this.delay(500);
    }
    
    /**
     * Execute single action via appropriate executor
     */
    private async executeAction(action: AIPlan): Promise<void> {
        // Ensure state is EnemyTurn
        if (!this.controlStateMachine.is(ControlState.EnemyTurn)) {
            this.controlStateMachine.setState(ControlState.EnemyTurn);
        }
        
        switch (action.type) {
            case 'move':
                if (!action.targetPath) {
                    console.error('[AITurnManager] Invalid move - missing hex');
                    return;
                }
                if (action.targetPath.cost === 0) {
                    console.log('[AITurnManager] Wait action executed');
                    break;
                }
                
                await this.executorManager.move(action.unitId, action.targetPath);
                break;
                
            case 'attack':
                if (!action.targetUnitId || !action.skillId) {
                    console.error('[AITurnManager] Invalid attack - missing target or skill');
                    return;
                }
                await this.executorManager.attack(
                    action.unitId, 
                    action.targetUnitId, 
                    action.skillId
                );
                break;
                
            case 'wait':
                // Wait action - do nothing
                console.log('[AITurnManager] Wait action executed');
                break;
                
            default:
                console.error('[AITurnManager] Unknown action type:', action);
        }
    }

    private async checkPause(): Promise<void> {
        // Якщо паузи немає — виходимо відразу (не чекаємо)
        if (useBattleStore.getState().status !== BattleStatus.Paused) return;

        console.log("[AITurnManager] Execution paused, waiting...");
        
        // Поки в сторі стоїть пауза — «спимо» короткими інтервалами
        while (useBattleStore.getState().status === BattleStatus.Paused) {
            await new Promise(resolve => setTimeout(resolve, 100)); 
        }
        
        console.log("[AITurnManager] Execution resumed");
    }
    
    /**
     * Check if should stop turn
     */
    private shouldStopTurn(
        unit: BattleUnit,
        unitId: string
    ): boolean {
        if (!unit || unit.isDead) {
            console.log(`[AITurnManager] Unit ${unitId} is dead or missing`);
            return true;
        }
        
        if (unit.currentStamina <= 0) {
            console.log(`[AITurnManager] Unit ${unitId} out of stamina`);
            return true;
        }
        
        const state = useBattleStore.getState();
        if (state.status !== BattleStatus.Playing && state.status !== BattleStatus.Paused) {
            console.log('[AITurnManager] Battle ended');
            return true;
        }
        
        return false;
    }
    
    /**
     * Detect infinite loops in action history
     */
    private detectLoop(
        actionHistory: string[],
        unitId: string,
        currentAction: AIPlan
    ): boolean {
        if (actionHistory.length < 5) return false;
        
        const last3 = actionHistory.slice(-3);
        const isLoop = last3[0] === last3[1] && last3[1] === last3[2];
        
        if (isLoop) {
            console.error(`[AITurnManager] LOOP DETECTED! Unit ${unitId} repeating:`, last3[0]);
            console.error('[AITurnManager] Action history:', actionHistory);
            console.error('[AITurnManager] Current action:', currentAction);
            return true;
        }
        
        return false;
    }
    
    /**
     * Verify stamina changed after action
     */
    private verifyStaminaChanged(unitId: string, staminaBefore: number): boolean {
        const unit = useBattleStore.getState().units.get(unitId);
        
        if (!unit) return false;
        
        if (unit.currentStamina === staminaBefore) {
            console.error(`[AITurnManager] WARNING: Stamina didn't change!`);
            console.error('[AITurnManager] This will cause infinite loop. Breaking.');
            return false;
        }
        
        return true;
    }
    
    /**
     * Finish unit turn and update state
     */
    private async finishUnitTurn(
        unitId: string,
        startState: BattleUnit,
        actionHistory: string[]
    ): Promise<void> {
        const state = useBattleStore.getState();
        
        await this.checkPause();
        
        if (state.status !== BattleStatus.Playing) {
            return;
        }

        const endState = state.units.get(unitId);

        console.log(`[AITurnManager] ===== Finished turn for ${unitId} =====`, {
            iterations: actionHistory.length,
            staminaUsed: (startState?.currentStamina || 0) - (endState?.currentStamina || 0),
            finalPosition: `${endState?.q}:${endState?.r}`,
            actions: actionHistory,
        });
        
        // Check if unit can still act
        if (endState && this.turnManager.canUnitStillAct(unitId)) {
            console.log(`[AITurnManager nextSameFactionIdx] Unit ${unitId} can still act - deferring`);
            this.turnManager.deferUnit(unitId);
        } else {
            console.log(`[AITurnManager nextSameFactionIdx] Unit ${unitId} cannot act - ending turn`);
            this.turnManager.endUnitTurn(unitId);
        }
    }
    
    /**
     * Utility: delay for animations
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    /**
     * Utility: log unit state
     */
    private logUnitState(
        label: string,
        unit: BattleUnit
    ): void {
        if (!unit) return;
        
        console.log(`[AITurnManager] ${label} state:`, {
            position: `${unit.q}:${unit.r}`,
            stamina: unit.currentStamina,
            hp: `${unit.currentUnitHp}/${unit.stats.hp}`,
            id: unit.unitId
        });
    }
}